// Phase FU-3 — Bank-to-Goal CTA wrapper.
//
// Source: SIKA_Independence_Architecture_v1.0 §10 Table 23 (Product
// Linking matrix) + §12 Table 24 row 21 (Cross-product win banking).
//
// Wraps the existing BankWinModal so 5 product surfaces (singles_chain,
// super_acca, powerball, owl_hour, daily_acca — flex already wired in
// RV-6b/SIKA-4b) can render a small "BANK TO GOAL" Pill on each settled
// WON slip row.
//
// Doctrine:
//   - N04 absolute: every banking action is user-initiated. No useEffect
//     fires bankWin — it only opens the modal on user click.
//   - Promotion-not-restriction: when the user has zero active SIKA
//     goals, the CTA routes them to /sika to create one (never renders
//     "Banking unavailable").
//   - Idempotent: the server-side UNIQUE (user_id, source, source_id)
//     constraint enforces re-banking safety. The wrapper mirrors that
//     locally — if the parent passes `alreadyBanked=true` we render a
//     small "✓ BANKED" Pill instead of the active CTA.
//   - No primitive edits, no --sa-* token mutations. Composes Pill +
//     BankWinModal only.

import { useState } from "react";
import { Link } from "react-router-dom";
import { Pill } from "../super-acca";
import { useSikaGoals } from "../../sika/useSikaGoals.ts";
import { BankWinModal } from "./BankWinModal.tsx";
import type { SikaBankingSource } from "../../sika/useBankExternalWin.ts";

export interface BankToGoalCtaProps {
  /** Source product the win came from. */
  source: SikaBankingSource;
  /** UUID of the won slip / chain / ticket. */
  sourceId: string;
  /** Amount available to bank (in GHS). Must be > 0. */
  amount: number;
  /** Optional one-line label about the win, used in the modal copy. */
  sourceLabel?: string;
  /** When true, render the "✓ BANKED" pill instead of the active CTA. */
  alreadyBanked?: boolean;
  /** Optional testid suffix; defaults to `${source}-${sourceId}`. */
  testIdSuffix?: string;
}

export function BankToGoalCta({
  source,
  sourceId,
  amount,
  sourceLabel,
  alreadyBanked,
  testIdSuffix,
}: BankToGoalCtaProps): JSX.Element | null {
  const { goals, loading } = useSikaGoals();
  const [open, setOpen] = useState(false);

  // Bail entirely if the win has no positive amount — never bank zero.
  if (!(amount > 0)) return null;
  // Bail when source_id is missing.
  if (!sourceId) return null;

  const suffix = testIdSuffix ?? `${source}-${sourceId}`;

  if (alreadyBanked === true) {
    return (
      <span data-testid={`bank-to-goal-banked-${suffix}`}>
        <Pill borderColor="var(--sa-hairline)" color="var(--sa-fg-muted)">
          ✓ BANKED
        </Pill>
      </span>
    );
  }

  if (loading) return null;

  const activeGoals = goals.filter(
    (g) => g.status === "active" || g.status === "in_progress",
  );

  // Promotion-not-restriction: route to /sika rather than hiding the CTA.
  if (activeGoals.length === 0) {
    return (
      <Link
        to="/sika"
        data-testid={`bank-to-goal-promote-${suffix}`}
        aria-label="Create a SIKA goal to bank wins"
        style={{
          background: "transparent",
          border: "1px solid var(--sa-accent-saffron)",
          color: "var(--sa-accent-saffron)",
          padding: "2px 8px",
          borderRadius: 3,
          fontFamily: "var(--sa-font-mono)",
          fontSize: 11,
          letterSpacing: "0.08em",
          textDecoration: "none",
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        CREATE A GOAL TO BANK WINS →
      </Link>
    );
  }

  return (
    <>
      <button
        type="button"
        data-testid={`bank-to-goal-cta-${suffix}`}
        onClick={() => setOpen(true)}
        aria-label="Bank toward goal"
        style={{
          background: "transparent",
          border: "1px solid var(--sa-accent-saffron)",
          color: "var(--sa-accent-saffron)",
          padding: "2px 8px",
          borderRadius: 3,
          fontFamily: "var(--sa-font-mono)",
          fontSize: 11,
          letterSpacing: "0.08em",
          cursor: "pointer",
        }}
      >
        🎯 BANK TO GOAL
      </button>
      {open ? (
        <BankWinModal
          source={source}
          sourceId={sourceId}
          amount={amount}
          sourceLabel={sourceLabel}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}
